import { Injectable } from '@angular/core';
import { SupabaseBaseHttpService } from '@examdojo/supabase';
import { from, map, Observable } from 'rxjs';
import { AssignmentStudentHttpModel } from './assignment-student.model';
import {
  AssignmentSectionQuestionHttpModel,
  AssignmentSectionWithQuestionsHttpModel,
  CreateAssignmentData,
  DuplicateAssignmentData,
  SaveSectionData,
  TopicAnalysisItem,
} from './class-assignment.model';

@Injectable({
  providedIn: 'root',
})
export class ClassAssignmentHttpService extends SupabaseBaseHttpService<'class_assignments', 'classroom'> {
  readonly schema = 'classroom';
  readonly tableName = 'class_assignments';
  private readonly viewName = 'teacher_assignments_list';
  private readonly sectionsViewName = 'teacher_assignment_sections_with_questions';
  private readonly studentsViewName = 'teacher_assignment_students_list';

  fetchAllAssignmentsView(options?: { classId?: string }) {
    let query = this.supabase.client.schema(this.schema).from(this.viewName).select('*');

    if (options?.classId) {
      query = query.eq('class_id', options.classId);
    }

    return from(query.throwOnError().then((response) => response.data!));
  }

  fetchAssignment(assignmentId: string) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.viewName)
        .select('*')
        .eq('assignment_id', assignmentId)
        .single()
        .throwOnError()
        .then((response) => response.data!),
    );
  }

  createAssignment(data: CreateAssignmentData) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('create_assignment', {
          p_assignment_name: data.name,
          p_class_id: data.classId,
        })
        .throwOnError()
        .then((response) => response.data as string | null),
    ).pipe(map((assignmentId) => assignmentId ?? ''));
  }

  duplicateAssignment(data: DuplicateAssignmentData) {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('duplicate_assignment', {
          p_source_assignment_id: data.sourceAssignmentId,
          p_name: data.name,
          p_target_class_id: data.classId,
        })
        .throwOnError()
        .then((response) => response.data as string | null),
    ).pipe(map((assignmentId) => assignmentId ?? ''));
  }

  /**
   * Soft delete assignment by setting archived_at timestamp.
   */
  override delete(assignmentId: string): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.tableName)
        .update({ archived_at: new Date().toISOString() })
        .eq('assignment_id', assignmentId)
        .throwOnError()
        .then(() => undefined),
    );
  }

  fetchSections(assignmentId: string): Observable<AssignmentSectionWithQuestionsHttpModel[]> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.sectionsViewName)
        .select('*')
        .eq('assignment_id', assignmentId)
        .order('position', { ascending: true })
        .throwOnError()
        .then((response) => {
          const data = response.data ?? [];
          return data.map((row) => ({
            ...row,
            questions: (row.questions as unknown as AssignmentSectionQuestionHttpModel[]) ?? [],
          }));
        }),
    );
  }

  saveSections(assignmentId: string, sections: SaveSectionData[]): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('save_assignment_sections', {
          p_assignment_id: assignmentId,
          p_sections: this.mapSectionsPayload(sections),
        })
        .throwOnError()
        .then(() => undefined),
    );
  }

  saveDraft(
    assignmentId: string,
    dueAt: Date | null,
    scheduledAtDraft: Date | null,
    sections: SaveSectionData[],
  ): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('save_assignment_draft', {
          p_assignment_id: assignmentId,
          // TODO: remove this cast once Supabase supports RPC arg nullability in generated Supabase types.
          p_due_at: (dueAt?.toISOString() ?? null) as string,
          p_scheduled_at_draft: (scheduledAtDraft?.toISOString() ?? null) as string,
          p_sections: this.mapSectionsPayload(sections),
        })
        .throwOnError()
        .then(() => undefined),
    );
  }

  publishAssignment(assignmentId: string, dueAt: Date): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('publish_assignment', {
          p_assignment_id: assignmentId,
          p_due_at: dueAt.toISOString(),
        })
        .throwOnError()
        .then(() => undefined),
    );
  }

  fetchAssignmentStudents(assignmentId: string): Observable<AssignmentStudentHttpModel[]> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .from(this.studentsViewName)
        .select('*')
        .eq('assignment_id', assignmentId)
        .throwOnError()
        .then((response) => response.data ?? []),
    );
  }

  fetchTopicAnalysis(assignmentId: string): Observable<TopicAnalysisItem[]> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('get_assignment_topic_analysis', {
          p_assignment_id: assignmentId,
        })
        .throwOnError()
        .then((response) => response.data ?? []),
    );
  }

  scheduleAssignment(assignmentId: string, scheduledAt: Date, dueAt: Date): Observable<void> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('schedule_assignment', {
          p_assignment_id: assignmentId,
          p_scheduled_at: scheduledAt.toISOString(),
          p_due_at: dueAt.toISOString(),
        })
        .throwOnError()
        .then(() => undefined),
    );
  }

  fetchTeacherAutoGradedCount(): Observable<number> {
    return from(
      this.supabase.client
        .schema(this.schema)
        .rpc('get_teacher_auto_graded_count')
        .throwOnError()
        .then((response) => (response.data as number) ?? 0),
    );
  }

  fetchWeeklyPublishMarksLimit(): Observable<number | null> {
    return from(
      this.supabase.client
        .from('settings')
        .select('classroom_weekly_publish_marks_limit')
        .limit(1)
        .maybeSingle()
        .throwOnError()
        .then((response) => response.data?.classroom_weekly_publish_marks_limit ?? null),
    );
  }

  private mapSectionsPayload(sections: SaveSectionData[]) {
    return sections.map((section) => ({
      id: section.id,
      name: section.name,
      position: section.position,
      question_ids: section.questionIds,
    }));
  }
}
